import React from 'react';
import Seo from '../components/Seo';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { getSiteSettings } from '../utils/cms';

const ContactPage = () => {
  const settings = getSiteSettings();

  const projectTypes = [
    'Badezimmer-Renovierung',
    'Küchen-Rückwand', 
    'Mosaik-Arbeiten',
    'Terrassenfliesen',
    'Reparatur/Sanierung',
    'Sonstiges'
  ];

  return (
    <>
      <Seo 
        title="Kontakt - Fliesen Jarek"
        description="Kontaktieren Sie Jarek Jozwiak für ein kostenloses und unverbindliches Angebot. Ihr Fliesenleger für Cuxhaven, Stade und Bremen."
      />
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-light text-stone-800 mb-4">Kontaktieren Sie uns</h1>
            <p className="text-lg text-stone-600 max-w-3xl mx-auto">Haben Sie Fragen oder möchten Sie ein Projekt besprechen? Wir freuen uns auf Ihre Nachricht.</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            {/* Contact Form */}
            <div className="bg-stone-50 p-8 rounded-lg">
              <h2 className="text-2xl font-semibold text-stone-800 mb-6">Anfrage senden</h2>
              
              <form 
                name="contact-page" 
                method="POST" 
                data-netlify="true" 
                data-netlify-honeypot="bot-field"
                action="/success"
                className="space-y-6"
              >
                {/* Netlify Forms required hidden fields */}
                <input type="hidden" name="form-name" value="contact-page" />
                
                {/* Honeypot field for spam protection */}
                <div style={{display: 'none'}}>
                  <label>
                    Don't fill this out if you're human: 
                    <input name="bot-field" />
                  </label>
                </div>

                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-stone-700 mb-2">
                    Name *
                  </label>
                  <input 
                    type="text" 
                    id="name"
                    name="name" 
                    required 
                    className="w-full p-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Ihr vollständiger Name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-stone-700 mb-2">
                    E-Mail *
                  </label>
                  <input 
                    type="email" 
                    id="email"
                    name="email" 
                    required 
                    className="w-full p-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="ihre@email.de"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-stone-700 mb-2">
                    Telefon
                  </label>
                  <input 
                    type="tel" 
                    id="phone"
                    name="phone" 
                    className="w-full p-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0151-12345678"
                  />
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-sm font-medium text-stone-700 mb-2">
                    Projektart
                  </label>
                  <select 
                    id="projectType"
                    name="projectType" 
                    className="w-full p-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  >
                    <option value="">Bitte wählen...</option>
                    {projectTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-stone-700 mb-2">
                    Nachricht *
                  </label>
                  <textarea 
                    id="message"
                    name="message" 
                    required 
                    rows="6"
                    className="w-full p-3 border border-stone-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder="Beschreiben Sie Ihr Projekt oder stellen Sie Ihre Frage..."
                  ></textarea>
                </div>

                <button 
                  type="submit" 
                  className="w-full bg-primary-600 text-white py-4 px-6 rounded-md hover:bg-primary-700 transition-colors duration-300 flex items-center justify-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>Nachricht senden</span>
                </button>
                
                <p className="text-xs text-stone-500 text-center">
                  * Pflichtfelder - Ihre Daten werden vertraulich behandelt
                </p>
              </form>
            </div>

            {/* Contact Info */}
            <div className="pt-8">
              <h3 className="text-2xl font-semibold text-stone-800 mb-6">Direkter Kontakt</h3>
              <div className="space-y-6">
                <div className="flex items-center">
                  <Phone className="w-6 h-6 text-primary-500 mr-4" />
                  <div>
                    <p className="text-lg font-semibold text-stone-800">{settings.phone}</p>
                    <p className="text-stone-600">Rufen Sie uns an</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Mail className="w-6 h-6 text-primary-500 mr-4" />
                  <div>
                    <p className="text-lg font-semibold text-stone-800">{settings.email}</p>
                    <p className="text-stone-600">Schreiben Sie uns eine E-Mail</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPin className="w-6 h-6 text-primary-500 mr-4" />
                  <div>
                    <p className="text-lg font-semibold text-stone-800">{settings.address.city}</p>
                    <p className="text-stone-600">Besuchen Sie uns (nach Vereinbarung)</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-6 bg-stone-100 rounded-lg">
                <h4 className="font-medium text-stone-800 mb-3">💡 Kostenlose Beratung</h4>
                <p className="text-stone-600 text-sm leading-relaxed">
                  Gerne komme ich zu Ihnen vor Ort für eine unverbindliche Beratung und Kostenvoranschlag. 
                  Rufen Sie einfach an oder schreiben Sie mir eine Nachricht!
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
